import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Company {
  id: number;
  name: string;
  city?: string;
  country?: string;
  manager_name?: string;
  manager_email?: string;
  status_id?: number;
  status_name?: string;
  is_active?: boolean;
  student_count?: number;
  supervisor_count?: number;
  account_count?: number;
  is_primary?: boolean;
  can_manage?: boolean;
}

export interface CompanyDetail extends Company {
  street?: string;
  zip?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  website?: string;
  tax_id?: string;
  logo?: string;
  translation_lang?: string;
  default_supervisor_id?: number;
  default_supervisor_name?: string;
  default_mandatory_supervision?: number;
  payment?: string;
  account_id?: number;
  info?: string;
  info1?: string;
  info2?: string;
  info5?: string; // Notes (JSON)
  created?: string;
  modified?: string;
  company_code?: string;
}

export interface AllowedCompany {
  id: number;
  name: string;
  is_primary: boolean;
  can_manage: boolean;
}

export interface AllowedCourse {
  id: number;
  name: string;
  is_primary: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CompaniesService {
  private apiUrl = environment.serverUrl;
  
  private _companies: BehaviorSubject<Company[]> = new BehaviorSubject<Company[]>([]);
  private _allowedCompanies: BehaviorSubject<AllowedCompany[]> = new BehaviorSubject<AllowedCompany[]>([]);
  private _allowedCourses: BehaviorSubject<AllowedCourse[]> = new BehaviorSubject<AllowedCourse[]>([]);
  private _selectedCompanyId: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private _isLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _isGlobalAdmin: boolean = false;
  private _userRole: number = 0;

  // Storage key for persisting selection
  private readonly STORAGE_KEY = 'selectedCompanyId';

  constructor(private http: HttpClient) {
    // Load persisted company selection
    this.loadPersistedSelection();
  }

  // Getters for observables
  get companies$(): Observable<Company[]> {
    return this._companies.asObservable();
  }

  get allowedCompanies$(): Observable<AllowedCompany[]> {
    return this._allowedCompanies.asObservable();
  }

  get selectedCompanyId$(): Observable<number | null> {
    return this._selectedCompanyId.asObservable();
  }

  get isLoading$(): Observable<boolean> {
    return this._isLoading.asObservable();
  }

  get allowedCourses$(): Observable<AllowedCourse[]> {
    return this._allowedCourses.asObservable();
  }

  /**
   * Get current selected company ID
   */
  getSelectedCompanyId(): number | null {
    return this._selectedCompanyId.value;
  }

  /**
   * Set user role for permission checks
   */
  setUserRole(role: number): void {
    this._userRole = role;
  }

  /**
   * Check if supervisor has no permissions at all (no companies and no courses)
   */
  get hasNoPermissions(): boolean {
    // Only relevant for supervisors (role 11)
    if (this._userRole !== 11) return false;
    return this._allowedCompanies.value.length === 0 && this._allowedCourses.value.length === 0;
  }

  /**
   * Set allowed companies from login response
   */
  setAllowedCompanies(companies: AllowedCompany[], isGlobalAdmin: boolean = false): void {
    this._allowedCompanies.next(companies);
    this._isGlobalAdmin = isGlobalAdmin;
    
    // If user has only one company and not global admin, auto-select it
    if (companies.length === 1 && !isGlobalAdmin) {
      const primaryCompany = companies.find(c => c.is_primary) || companies[0];
      if (primaryCompany && this._selectedCompanyId.value === null) {
        this.selectCompany(primaryCompany.id);
      }
    }
  }

  /**
   * Set allowed courses from login response (for supervisors)
   */
  setAllowedCourses(courses: AllowedCourse[]): void {
    this._allowedCourses.next(courses);
  }

  /**
   * Get allowed courses
   */
  getAllowedCourses(): AllowedCourse[] {
    return this._allowedCourses.value;
  }

  /**
   * Get allowed companies
   */
  getAllowedCompanies(): AllowedCompany[] {
    return this._allowedCompanies.value;
  }

  /**
   * Check if current user is a global admin (can see all companies)
   */
  isGlobalAdmin(): boolean {
    return this._isGlobalAdmin;
  }

  /**
   * Check if user has access to multiple companies (for showing dropdown vs badge)
   */
  hasMultipleCompanies(): boolean {
    return this._isGlobalAdmin || this._allowedCompanies.value.length > 1;
  }

  /**
   * Load companies from API with optional search
   */
  loadCompanies(search: string = '', includeInactive: boolean = false): Observable<Company[]> {
    this._isLoading.next(true);
    
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (includeInactive) {
      params = params.set('include_inactive', 'true');
    }
    
    return this.http.get<any>(`${this.apiUrl}/v1/companies`, { params }).pipe(
      map(response => response.data || []),
      tap(companies => {
        this._companies.next(companies);
        this._isLoading.next(false);
      }),
      catchError(error => {
        console.error('Error loading companies:', error);
        this._isLoading.next(false);
        return of([]);
      })
    );
  }

  /**
   * Get single company details
   */
  getCompany(id: number): Observable<CompanyDetail> {
    return this.http.get<any>(`${this.apiUrl}/v1/companies/${id}`).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create new company
   */
  createCompany(data: Partial<CompanyDetail>): Observable<CompanyDetail> {
    return this.http.post<any>(`${this.apiUrl}/v1/companies`, data).pipe(
      map(response => response.data),
      tap(() => {
        // Reload companies list
        this.loadCompanies().subscribe();
      })
    );
  }

  /**
   * Update company
   */
  updateCompany(id: number, data: Partial<CompanyDetail>): Observable<CompanyDetail> {
    return this.http.put<any>(`${this.apiUrl}/v1/companies/${id}`, data).pipe(
      map(response => response.data),
      tap(() => {
        // Reload companies list
        this.loadCompanies().subscribe();
      })
    );
  }

  /**
   * Delete company
   */
  deleteCompany(id: number): Observable<void> {
    return this.http.delete<any>(`${this.apiUrl}/v1/companies/${id}`).pipe(
      tap(() => {
        // Reload companies list
        this.loadCompanies().subscribe();
        // Clear selection if deleted company was selected
        if (this._selectedCompanyId.value === id) {
          this.clearSelection();
        }
      })
    );
  }

  /**
   * Activate company
   */
  activateCompany(id: number): Observable<CompanyDetail> {
    return this.http.post<any>(`${this.apiUrl}/v1/companies/${id}/activate`, {}).pipe(
      map(response => response.data),
      tap(() => {
        // Reload companies list
        this.loadCompanies().subscribe();
      })
    );
  }

  /**
   * Regenerate the company access code
   */
  regenerateCode(id: number): Observable<{ success: boolean; company_code: string }> {
    return this.http.post<{ success: boolean; company_code: string }>(
      `${this.apiUrl}/v1/companies/${id}/regenerate-code`, {}
    );
  }

  /**
   * Select a company
   */
  selectCompany(companyId: number | null): void {
    this._selectedCompanyId.next(companyId);
    
    // Persist selection
    if (companyId !== null) {
      sessionStorage.setItem(this.STORAGE_KEY, companyId.toString());
    } else {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Clear company selection (show all)
   */
  clearSelection(): void {
    this.selectCompany(null);
  }

  /**
   * Load persisted company selection from storage
   */
  private loadPersistedSelection(): void {
    const stored = sessionStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      const companyId = parseInt(stored, 10);
      if (!isNaN(companyId)) {
        this._selectedCompanyId.next(companyId);
      }
    }
  }

  /**
   * Get company by ID from loaded companies
   */
  getCompanyById(id: number): Company | undefined {
    return this._companies.value.find(c => c.id === id);
  }

  /**
   * Get selected company name
   */
  getSelectedCompanyName(): string {
    const companyId = this._selectedCompanyId.value;
    if (companyId === null) {
      return 'All Companies';
    }
    const company = this.getCompanyById(companyId) || 
                    this._allowedCompanies.value.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  }

  /**
   * Require a company to be selected (for create actions)
   * Returns true if a specific company is selected, false if "All Companies"
   */
  requireCompanySelected(): boolean {
    return this._selectedCompanyId.value !== null;
  }

  /**
   * Get the effective company ID for creating entities
   * Returns the selected company ID, or the primary company if none selected
   */
  getEffectiveCompanyId(): number | null {
    const selectedId = this._selectedCompanyId.value;
    if (selectedId !== null) {
      return selectedId;
    }
    
    // If global admin with no selection, return null (must select first)
    if (this._isGlobalAdmin) {
      return null;
    }
    
    // Return primary company for non-global admins
    const primaryCompany = this._allowedCompanies.value.find(c => c.is_primary);
    return primaryCompany?.id || this._allowedCompanies.value[0]?.id || null;
  }

  /**
   * Get permitted companies as Company[] for use in dropdowns.
   * For global admins: calls the API (loadCompanies).
   * For everyone else: returns allowedCompanies from JWT (no API call).
   */
  getPermittedCompanies(): Observable<Company[]> {
    if (this._isGlobalAdmin) {
      return this.loadCompanies();
    }
    // Convert AllowedCompany[] to Company[] for compatibility
    return this._allowedCompanies.asObservable().pipe(
      map(allowed => allowed.map(c => ({ id: c.id, name: c.name, is_primary: c.is_primary, can_manage: c.can_manage } as Company)))
    );
  }

  // ---------- Company Account Management (Course-Admin) ----------

  /**
   * Get company's admin account details
   */
  getCompanyAccount(companyId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/v1/companies/${companyId}/account`).pipe(
      map(response => response.data)
    );
  }

  /**
   * Create admin account for company
   */
  createCompanyAccount(companyId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/companies/${companyId}/account`, {}).pipe(
      map(response => response)
    );
  }

  /**
   * Send account info email to company admin
   */
  sendCompanyAccountInfo(companyId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/companies/${companyId}/account/send-info`, {});
  }

  /**
   * Regenerate password for company admin account
   */
  regenerateCompanyAccountPassword(companyId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v1/companies/${companyId}/account/regenerate-password`, {}).pipe(
      map(response => response)
    );
  }
}
